// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useState } from "react";
import { useAuthSession } from "../../../hooks/useAuthSession";
import { getBillingPortalUrl } from "../api/billingApi";
import { isDevPreviewMode } from "../../../utils/devEnv";
import { getRuntimeEnvValue, isTestMode } from "../../../utils/runtimeEnv";

export interface BillingPortalHook {
  url: string | undefined;
  isResolving: boolean;
  openPortal: () => Promise<void>;
}

export function useBillingPortalUrl(): BillingPortalHook {
  const { getAccessToken, isConfigured } = useAuthSession();
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [isResolving, setIsResolving] = useState(false);

  const openPortal = useCallback(async (): Promise<void> => {
    if (isTestMode() || isDevPreviewMode || !isConfigured) {
      const marketing = getRuntimeEnvValue("VITE_MARKETING_BASE_URL") ?? "";
      if (marketing.length > 0) {
        window.open(`${marketing.replace(/\/$/, "")}/pricing`, "_blank", "noopener");
      }
      return;
    }

    const popup = window.open("", "_blank");
    if (popup !== null) {
      popup.opener = null;
    }

    setIsResolving(true);
    try {
      const token = await getAccessToken();
      const portalUrl = await getBillingPortalUrl({ token });
      if (portalUrl) {
        setUrl(portalUrl);
        if (popup !== null) {
          popup.location.href = portalUrl;
        } else {
          window.open(portalUrl, "_blank", "noopener");
        }
      } else {
        popup?.close();
      }
    } catch {
      popup?.close();
    } finally {
      setIsResolving(false);
    }
  }, [getAccessToken, isConfigured]);

  return { url, isResolving, openPortal };
}
